import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EtapaAprovacaoGenerica from './EtapaAprovacaoGenerica';
import { apiFetch } from '../../api/client';
import { TIPOS_ACAO_ETAPA_LABEL } from '../../pages/contratos/utils/fluxoEtapaTipos';

const CHECKLIST_AUDITORIA = [
  { key: 'tr', label: 'TR presente' },
  { key: 'edital', label: 'Edital configurado' },
  { key: 'mapa', label: 'Mapa comparativo concluído' },
  { key: 'aprovacoes', label: 'Aprovações anteriores registradas' },
];

/**
 * Painel de etapa baseado em tipo_acao (Configuração de Fluxo).
 * Persistência apenas via API /extensoes — sem localStorage.
 */
export default function PainelEtapaAprovacaoFluxo({
  processoId,
  tipoAcao,
  tituloEtapa = '',
  dadosSolicitante = null,
  onDecisao = null,
  onSaved = null,
}) {
  const tipo = String(tipoAcao || '').toUpperCase();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const [ext, setExt] = useState(null);

  // Campos específicos
  const [parecerTecnico, setParecerTecnico] = useState('');
  const [dotacaoCodigo, setDotacaoCodigo] = useState('');
  const [dotacaoValor, setDotacaoValor] = useState('');
  const [checklist, setChecklist] = useState({
    tr: false,
    edital: false,
    mapa: false,
    aprovacoes: false,
  });

  const carregar = useCallback(async () => {
    if (!processoId) return;
    setLoading(true);
    setErro('');
    try {
      const res = await apiFetch(`/api/contratos/processos/${processoId}/extensoes`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha ao carregar etapa');
      setExt(data);
      setParecerTecnico(data.parecerTecnico || data.minuta?.parecerTecnico || '');
      const dot = data.dotacaoOrcamentaria || {};
      setDotacaoCodigo(dot.codigo || '');
      setDotacaoValor(dot.valorReservado != null ? String(dot.valorReservado) : '');
      setChecklist({
        tr: !!data.objetoTr,
        edital: !!(data.editalAtual && data.editalAtual.dataAbertura),
        mapa: !!data.mapaComparativoConcluido,
        aprovacoes: !!(dot.aprovadoPor || data.auditoriaStatus),
      });
    } catch (e) {
      setErro(e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [processoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const titulo = useMemo(() => {
    if (tituloEtapa) return tituloEtapa;
    return TIPOS_ACAO_ETAPA_LABEL[tipo] || 'Aprovação da etapa';
  }, [tituloEtapa, tipo]);

  const persistirAntes = async (acao, observacao) => {
    const body = {};
    if (tipo === 'ANALISE_TECNICA') {
      body.parecerTecnico = parecerTecnico || observacao || null;
    }
    if (tipo === 'CONFIGURACAO_DOTACAO') {
      if (acao === 'APROVAR' && !String(dotacaoCodigo || '').trim()) {
        throw new Error('Informe o código da dotação orçamentária.');
      }
      body.dotacaoOrcamentaria = {
        codigo: String(dotacaoCodigo || '').trim(),
        valorReservado: dotacaoValor !== '' ? Number(String(dotacaoValor).replace(',', '.')) : null,
        aprovadoPor: acao === 'APROVAR' ? 'etapa' : null,
        data: new Date().toISOString(),
        observacao: observacao || null,
        status: acao === 'APROVAR' ? 'APROVADO' : 'REPROVADO',
      };
    }
    if (tipo === 'AUDITORIA') {
      if (acao === 'APROVAR') {
        const faltando = CHECKLIST_AUDITORIA.filter((c) => !checklist[c.key]);
        if (faltando.length) {
          throw new Error(`Checklist incompleto: ${faltando.map((f) => f.label).join(', ')}`);
        }
      }
      body.auditoriaStatus = acao === 'APROVAR' ? 'APROVADO' : 'REPROVADO';
      body.auditoriaChecklist = checklist;
      body.auditoriaObservacao = observacao || null;
    }

    // Aprovação simples / com assinatura: observação vai no /mover, não em /extensoes.
    // Antes: body vazio + observação preenchida → PUT {} → "Nenhum campo para atualizar."
    if (!Object.keys(body).length) return null;

    // Campos extras de auditoria/parecer vão em payload estendido via extensoes + campos já suportados
    const payload = { ...body };
    if (payload.parecerTecnico !== undefined || payload.auditoriaChecklist || payload.auditoriaObservacao) {
      payload.minuta = {
        ...(ext?.minuta && typeof ext.minuta === 'object' ? ext.minuta : {}),
        parecerTecnico: payload.parecerTecnico ?? ext?.minuta?.parecerTecnico,
        auditoriaChecklist: payload.auditoriaChecklist,
        auditoriaObservacao: payload.auditoriaObservacao,
      };
      delete payload.parecerTecnico;
      delete payload.auditoriaChecklist;
      delete payload.auditoriaObservacao;
    }

    const res = await apiFetch(`/api/contratos/processos/${processoId}/extensoes`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao salvar decisão da etapa');
    onSaved?.(data.processo || null);
    return data.processo;
  };

  const handleAprovar = async (observacao) => {
    setSaving(true);
    setErro('');
    try {
      await persistirAntes('APROVAR', observacao);
      await onDecisao?.({ acao: 'APROVAR', observacao, tipoAcao: tipo });
    } catch (e) {
      setErro(e.message || 'Falha ao aprovar');
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleReprovar = async (observacao) => {
    setSaving(true);
    setErro('');
    try {
      await persistirAntes('REJEITAR', observacao);
      await onDecisao?.({ acao: 'REJEITAR', observacao, tipoAcao: tipo });
    } catch (e) {
      setErro(e.message || 'Falha ao reprovar');
      throw e;
    } finally {
      setSaving(false);
    }
  };

  // Etapas que não usam este card (já têm painéis próprios)
  if (['CONFIGURACAO_EDITAL', 'MAPA_COMPARATIVO', 'MINUTA_CLAUSULAS', 'PREENCHIMENTO_FORMULARIO'].includes(tipo)) {
    return null;
  }

  if (!processoId || !tipo) return null;

  const camposEspecificos = (() => {
    if (tipo === 'ANALISE_TECNICA') {
      return (
        <TextField
          label="Parecer técnico"
          fullWidth
          multiline
          minRows={3}
          size="small"
          value={parecerTecnico}
          onChange={(e) => setParecerTecnico(e.target.value)}
          helperText="Avaliação técnica do fiscal sobre a proposta mais vantajosa"
        />
      );
    }
    if (tipo === 'CONFIGURACAO_DOTACAO') {
      return (
        <Stack spacing={1.5}>
          <TextField
            label="Código da dotação"
            fullWidth
            size="small"
            required
            value={dotacaoCodigo}
            onChange={(e) => setDotacaoCodigo(e.target.value)}
          />
          <TextField
            label="Valor reservado (R$)"
            fullWidth
            size="small"
            type="number"
            value={dotacaoValor}
            onChange={(e) => setDotacaoValor(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
          />
        </Stack>
      );
    }
    if (tipo === 'AUDITORIA') {
      return (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Checklist obrigatório
          </Typography>
          <Stack>
            {CHECKLIST_AUDITORIA.map((item) => (
              <FormControlLabel
                key={item.key}
                control={
                  <Checkbox
                    checked={!!checklist[item.key]}
                    onChange={(e) =>
                      setChecklist((prev) => ({ ...prev, [item.key]: e.target.checked }))
                    }
                  />
                }
                label={item.label}
              />
            ))}
          </Stack>
          {ext?.auditoriaStatus && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Status atual: {ext.auditoriaStatus}
            </Alert>
          )}
        </Box>
      );
    }
    return null;
  })();

  return (
    <Box sx={{ mt: 2 }}>
      {erro && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setErro('')}>
          {erro}
        </Alert>
      )}
      <EtapaAprovacaoGenerica
        titulo={titulo}
        subtitulo={`Tipo de ação: ${TIPOS_ACAO_ETAPA_LABEL[tipo] || tipo}`}
        dadosSolicitante={dadosSolicitante}
        loading={loading || saving}
        onAprovar={handleAprovar}
        onReprovar={handleReprovar}
      >
        {camposEspecificos}
      </EtapaAprovacaoGenerica>
    </Box>
  );
}
